// The running week, drawn from the plan each person sets (lib/run-schedule.js).
// A progress bar for the week, then one row per planned run. A row opens to
// "Log it" and an optional "Change next run". All text is app-generated (no user
// input), so no escaping is needed here.
import { icon } from './icons.js';
import { planSlotDone } from '../lib/run-metrics.js';

const WD = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function weekBounds(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const g = d.getDay(); const monDiff = g === 0 ? 6 : g - 1;
  const mon = new Date(d); mon.setDate(d.getDate() - monDiff);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { weekStartISO: iso(mon), weekEndISO: iso(sun) };
}

export function renderRunWeek({ week, runs, canLog, footNote = '' }) {
  const { weekStartISO, weekEndISO } = weekBounds();
  const done = week.runs.map((r) => planSlotDone(runs, { weekStartISO, weekEndISO, weekday: r.weekday, slot: r.slot }));
  const nDone = done.filter(Boolean).length;
  const pct = week.runs.length ? Math.round((nDone / week.runs.length) * 100) : 0;

  const rows = week.runs.map((r, i) => {
    const marker = done[i]
      ? `<span class="flex-none w-7 h-7 rounded-full bg-success text-white flex items-center justify-center">${icon.check('w-4 h-4')}</span>`
      : '<span class="flex-none w-7 h-7 rounded-full border-2 border-brand"></span>';
    const target = r.unit === 'min' ? `${r.value} min` : `${r.value} mi`;
    return `<div class="border-t border-hairline first:border-0">
      <button data-open="${i}" class="grit-row w-full flex items-center gap-3 px-4 min-h-[60px] py-2 text-left">
        ${marker}
        <div class="flex-1 min-w-0"><div class="font-semibold text-[17px] text-ink">${WD[r.weekday]} · ${r.title}</div>
          <div class="text-[13px] ${done[i] ? 'text-success' : 'text-inksoft'} truncate">${done[i] ? 'Done this week' : r.detail}</div></div>
        <span class="text-[15px] font-semibold text-ink flex-none tabular-nums">${target}</span>
      </button>
      <div data-body="${i}" class="hidden px-4 pb-4">
        ${canLog ? `<div class="flex gap-2">
          <button data-log="${i}" class="grit-btn flex-1">${done[i] ? 'Log again' : 'Log it'}</button>
          <button data-change="${i}" class="grit-btn-quiet flex-1">Change next run</button>
        </div>` : ''}
      </div>
    </div>`;
  }).join('');

  return `
    <div class="grit-card p-4">
      <div class="flex justify-between text-[15px] mb-1.5"><span class="text-inksoft">This week</span><span class="font-semibold text-ink">${nDone} of ${week.runs.length} done</span></div>
      <div class="h-2 rounded-full bg-fill overflow-hidden"><div class="h-full bg-brand rounded-full" style="width:${pct}%"></div></div>
    </div>
    <div class="grit-card p-0 mt-3 overflow-hidden">
      <div class="flex items-center justify-between px-4 pt-3 pb-1">
        <div class="text-[17px] font-semibold text-ink">Your runs</div>
        ${canLog ? '<button id="edit-plan" class="text-grit text-[15px] font-semibold min-h-[44px] px-1">Edit plan</button>' : ''}
      </div>
      ${rows || '<div class="px-4 pb-4 text-[15px] text-inksoft">No runs planned. Tap Edit plan to add one.</div>'}
      ${footNote ? `<div class="text-[13px] text-inksoft px-4 pt-1 pb-4">${footNote}</div>` : '<div class="pb-2"></div>'}
    </div>`;
}

// Bind after injecting the HTML. `week` is the same object passed to renderRunWeek.
export function bindRunWeek(el, { week, onLog, onChange }) {
  el.querySelectorAll('[data-open]').forEach((b) => b.onclick = () => {
    const body = el.querySelector(`[data-body="${b.dataset.open}"]`);
    if (body) body.classList.toggle('hidden');
  });
  el.querySelectorAll('[data-log]').forEach((b) => b.onclick = () => onLog(week.runs[Number(b.dataset.log)]));
  el.querySelectorAll('[data-change]').forEach((b) => b.onclick = () => onChange?.(week.runs[Number(b.dataset.change)]));
}
